/**
 * Sidebar v2 drag controller: the drag state machine, the sortable list
 * model, and the drop writers.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (drag wiring and handleThreadDragEnd, ~lines 3200-3710) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: order-key and lifecycle writes land on
 * forge's client-side overlay (uiStateStore) instead of upstream's
 * server-side thread commands; forge's settle path stays server-side.
 */
import { useCallback, useMemo, useState } from "react";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";

import { planSidebarThreadDrop } from "./sidebarV2DropPlan";
import { applyThreadDragPlan } from "./sidebarV2DragApply";
import type { SidebarSection } from "./sidebarV2List";
import { resolveSidebarDropTarget, type SidebarListItem } from "./sidebarV2List";
import { sidebarThreadKey, type SidebarV2Thread } from "./sidebarV2Threads";

export interface SidebarV2DragState {
  readonly activeKey: string;
  readonly activeSection: SidebarSection;
  readonly occurredAt: string;
  readonly targetSection: SidebarSection | null;
}

export interface SidebarV2DragResult {
  readonly dragState: SidebarV2DragState | null;
  readonly sidebarListItems: readonly SidebarListItem[];
  readonly handleThreadDragStart: (activeKey: string) => boolean;
  readonly handleThreadDragOver: (activeId: string, overId: string | null) => void;
  readonly handleThreadDragEnd: (activeId: string, overId: string | null) => void;
}

/** Build the sortable shelf list and translate drag start, hover, and drop events into thread actions. */
export function useSidebarV2DragController(input: {
  readonly overlaidThreads: readonly SidebarV2Thread[];
  readonly pinnedThreads: readonly SidebarV2Thread[];
  readonly activeThreads: readonly SidebarV2Thread[];
  readonly snoozedThreads: readonly SidebarV2Thread[];
  readonly settledThreads: readonly SidebarV2Thread[];
  readonly visibleSettledThreads: readonly SidebarV2Thread[];
  readonly snoozedExpanded: boolean;
  readonly settledExpanded: boolean;
  readonly renamingThreadKey: string | null;
  readonly attemptSettle: (threadRef: ReturnType<typeof scopeThreadRef>) => void;
  readonly attemptUnsettle: (threadRef: ReturnType<typeof scopeThreadRef>) => void;
  readonly pinThread: (threadRef: ReturnType<typeof scopeThreadRef>) => void;
  readonly unpinThread: (threadRef: ReturnType<typeof scopeThreadRef>) => void;
  readonly setThreadPinned: (threadId: string, pinned: boolean) => void;
  readonly setThreadSnooze: (threadId: string, snooze: null) => void;
  readonly setThreadPinOrderKey: (threadId: string, orderKey: string | null) => void;
  readonly setThreadActiveOrderKey: (threadId: string, orderKey: string | null) => void;
  readonly threadByKey: ReadonlyMap<string, SidebarV2Thread>;
}): SidebarV2DragResult {
  const [dragState, setDragState] = useState<SidebarV2DragState | null>(null);
  const sidebarListItems = useMemo((): readonly SidebarListItem[] => {
    /** Convert a shelf of threads into list entries keyed by the canonical scoped key. */
    const rowsOf = (list: readonly SidebarV2Thread[], section: SidebarSection): SidebarListItem[] =>
      list.map((thread) => ({
        kind: "thread" as const,
        key: sidebarThreadKey(thread),
        section,
      }));
    if (
      input.pinnedThreads.length +
        input.activeThreads.length +
        input.snoozedThreads.length +
        input.settledThreads.length ===
      0
    ) {
      return [];
    }
    const items: SidebarListItem[] = [{ kind: "marker", marker: "pinned-header" }];
    items.push(...rowsOf(input.pinnedThreads, "pinned"));
    items.push({ kind: "marker", marker: "pinned-divider" });
    items.push({ kind: "marker", marker: "active-placeholder" });
    items.push(...rowsOf(input.activeThreads, "active"));
    // A collapsed shelf keeps only its header: the rows stay out of the
    // list (and out of the drag and projection models) until expanded. The
    // settled shelf keeps its placeholder so it remains a drop target.
    if (input.snoozedThreads.length > 0) {
      items.push({ kind: "marker", marker: "snoozed-header" });
      if (input.snoozedExpanded) items.push(...rowsOf(input.snoozedThreads, "snoozed"));
    }
    items.push({ kind: "marker", marker: "settled-header" });
    items.push({ kind: "marker", marker: "settled-placeholder" });
    if (input.settledExpanded) items.push(...rowsOf(input.visibleSettledThreads, "settled"));
    return items;
  }, [
    input.activeThreads,
    input.pinnedThreads,
    input.settledExpanded,
    input.settledThreads,
    input.snoozedExpanded,
    input.snoozedThreads,
    input.visibleSettledThreads,
  ]);

  const keysById = useMemo(
    () => ({
      pinned: new Map(
        input.overlaidThreads.map(
          (thread) => [sidebarThreadKey(thread), thread.pinOrderKey] as const,
        ),
      ),
      active: new Map(
        input.overlaidThreads.map(
          (thread) => [sidebarThreadKey(thread), thread.activeOrderKey] as const,
        ),
      ),
    }),
    [input.overlaidThreads],
  );
  const pinnedKeys = useMemo(
    () => input.pinnedThreads.map((thread) => sidebarThreadKey(thread)),
    [input.pinnedThreads],
  );
  const activeKeys = useMemo(
    () => input.activeThreads.map((thread) => sidebarThreadKey(thread)),
    [input.activeThreads],
  );

  const handleThreadDragStart = useCallback(
    (activeKey: string) => {
      // The lifted row's own list entry decides the section: snoozed and
      // settled rows drag like pinned and active ones, and a row hidden by
      // a collapsed shelf cannot start a drag at all.
      const item = sidebarListItems.find(
        (entry) => entry.kind === "thread" && entry.key === activeKey,
      );
      if (item?.kind !== "thread") return false;
      setDragState({
        activeKey,
        activeSection: item.section,
        targetSection: item.section,
        occurredAt: new Date().toISOString(),
      });
      return true;
    },
    [sidebarListItems],
  );
  const handleThreadDragOver = useCallback(
    (activeId: string, overId: string | null) => {
      const target =
        overId === null ? null : resolveSidebarDropTarget(sidebarListItems, activeId, overId);
      setDragState((current) =>
        current === null || current.activeKey !== activeId
          ? current
          : { ...current, targetSection: target?.section ?? null },
      );
    },
    [sidebarListItems],
  );
  const handleThreadDragEnd = useCallback(
    (activeId: string, overId: string | null) => {
      const state = dragState;
      setDragState(null);
      if (state === null) return;
      const target =
        overId === null ? null : resolveSidebarDropTarget(sidebarListItems, activeId, overId);
      const thread = input.threadByKey.get(activeId);
      if (target === null || thread === undefined) return;
      const threadRef = scopeThreadRef(thread.environmentId, thread.id);
      const plan = planSidebarThreadDrop({
        activeKey: activeId,
        activeSection: state.activeSection,
        activePinned: thread.pinnedAt != null,
        activeSettled: thread.settledOverride === "settled",
        target,
        pinnedOrder: pinnedKeys,
        pinnedKeysById: keysById.pinned,
        activeOrder: activeKeys,
        activeKeysById: keysById.active,
      });
      if (plan.kind === "none") return;
      applyThreadDragPlan(plan, {
        activeId,
        threadRef,
        attemptSettle: input.attemptSettle,
        attemptUnsettle: input.attemptUnsettle,
        pinThread: input.pinThread,
        setThreadPinned: input.setThreadPinned,
        setThreadSnooze: input.setThreadSnooze,
        setThreadPinOrderKey: input.setThreadPinOrderKey,
        setThreadActiveOrderKey: input.setThreadActiveOrderKey,
      });
    },
    [activeKeys, dragState, input, keysById, pinnedKeys, sidebarListItems],
  );

  return {
    dragState,
    sidebarListItems,
    handleThreadDragStart,
    handleThreadDragOver,
    handleThreadDragEnd,
  };
}
